import { NgClass, NgStyle } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, signal, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe } from 'angular-l10n';
import { Subscription, debounceTime, map } from 'rxjs';
import { Tenant, TenantApi } from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';

@Component({
  selector: 'app-tenant-search',
  templateUrl: './tenant-search.component.html',
  styleUrls: ['./tenant-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: true,
  imports: [NgClass, NgStyle, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule, MatIconModule, MatButtonModule, MatTooltipModule, L10nTranslatePipe],
})
export class TenantSearchComponent implements OnInit, OnChanges {
  private readonly tenantApi = inject(TenantApi);
  private readonly authService = inject(AuthService);
  locale = inject<L10nLocale>(L10N_LOCALE);

  @Output() tenantSelected = new EventEmitter<Tenant>();
  @Output() formChange = new EventEmitter<string>();
  @Output() enterPressed = new EventEmitter<string>();

  @Input() showDisabled = false;
  @Input() waitForValue = false;
  @Input() resetOnSelect = false;
  @Input() initialTenantId: string;
  @Input() tenantId?: string;
  @Input() placeholder = 'Find tenants...';
  @Input() hint: string;
  @Input() useDefaultStyling = false;
  @Input() inputStyle: Record<string, string> = {};
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() width: string;

  subscriptions = new Subscription();
  get isSuperAdmin(): boolean {
    return this.authService.isSuperAdmin();
  }

  tenants = signal<Tenant[]>([]);
  tenantSearch: FormControl<string> = new FormControl(null);
  selectedTenantId = signal<number>(null);

  ngOnInit(): void {
    if (!this.waitForValue) this.suggestTenants(this.initialTenantId || '');
    if (this.initialTenantId) {
      this.getTenantById(this.initialTenantId);
    }

    this.subscriptions.add(
      this.tenantSearch.valueChanges.pipe(debounceTime(300)).subscribe((value: string) => {
        this.formChange.emit(value);
        this.suggestTenants(value);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    const tenantId = changes['tenantId']?.currentValue;
    if (tenantId) {
      this.getTenantById(tenantId);
    }
  }

  private getTenantById(tenantId: string) {
    const tenantIdInt = parseInt(tenantId, 10);
    if (!isNaN(tenantIdInt) && tenantIdInt >= 0) {
      if (tenantIdInt === this.selectedTenantId()) {
        return;
      }

      this.selectedTenantId.set(tenantIdInt);
      this.tenantApi.getTenantById(tenantIdInt.toString()).subscribe((r) => {
        if (!r.hasErrors && r.isValid) {
          this.onTenantSelect(r.item);
        }
      });
    }
  }

  suggestTenants(search: string): void {
    const trimmedSearch = search?.trim();
    const tenantId = parseInt(trimmedSearch, 10);
    const tenantIdParamValue = !!tenantId && !isNaN(tenantId) ? [tenantId] : null;
    const searchParamValue = tenantId ? null : trimmedSearch;
    this.tenantApi
      .getTenants(tenantIdParamValue, null, searchParamValue, null, null, this.showDisabled, 1, 10)
      .pipe(map((response) => response.items))
      .subscribe((tenants) => {
        this.tenants.set(tenants);
      });
  }

  onEnter() {
    this.enterPressed.emit(this.tenantSearch.value);
  }

  onTenantSelect(tenant: Tenant): void {
    this.selectedTenantId.set(tenant?.id);
    this.tenantSelected.emit(tenant);
    if (this.resetOnSelect) {
      this.tenantSearch.setValue(null);
      this.tenants.set([]);
    } else {
      this.tenantSearch.setValue(this.getTenantFieldValue(tenant));
    }
  }

  getTenantFieldValue(tenant: Tenant) {
    if (!tenant) return '';
    return `(${tenant?.id}) ${tenant?.name}`;
  }

  clearSelectedTenant() {
    this.tenantSearch.setValue('');
    this.onTenantSelect(null);
  }
}
